/**
 * @fileoverview NativeDropdownRenderer component for the Storyteller web application.
 * Renders a native dropdown/select input with translated options and value handling.
 *
 * @example
 * // Basic usage
 * <NativeDropdownRenderer
 *   schema={{
 *     type: 'string',
 *     oneOf: [
 *       { const: 'option1', title: 'Option 1' },
 *       { const: 'option2', title: 'Option 2' }
 *     ]
 *   }}
 *   uischema={{
 *     options: {
 *       name: 'dropdown',
 *       placeholder: 'Select an option'
 *     }
 *   }}
 *   path="dropdown"
 *   handleChange={(path, value) => console.log(path, value)}
 * />
 */

import { ControlProps, JsonSchema4, rankWith, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsControlProps } from '@jsonforms/react'
import { useTranslation } from 'react-i18next'
import { NativeDropdown } from '../../../base/NativeDropdown'

/**
 * @interface NativeDropdownProps
 * @description Interface for the NativeDropdownRenderer component.
 * @property {JsonSchema4} schema - The schema for the native dropdown.
 * @property {string} data - The data for the native dropdown.
 * @property {Record<string, string>} uischema - The UI schema for the native dropdown.
 * @property {Record<string, string>} errors - The errors for the native dropdown.
 * @property {function} handleChange - The function to handle the change of the native dropdown.
 * @property {string} path - The path of the native dropdown.
 * @property {boolean} required - Whether the native dropdown is required.
 */
interface NativeDropdownProps extends ControlProps {
    label: string
    required?: boolean
    handleChange(path: string, value: string): void
    path: string
}

/**
 * @function NativeDropdownRenderer
 * @description Component for rendering a native dropdown form element.
 * @param {NativeDropdownProps} props - The props for the NativeDropdownRenderer component.
 * @returns {JSX.Element} The NativeDropdownRenderer component.
 */
function NativeDropdownRenderer(props: NativeDropdownProps) {
    const { schema, data, uischema, errors, handleChange, path, required } = props
    const { t } = useTranslation()

    const value = data || schema.default || ''

    function onChange(value: unknown) {
        let formattedValue = value
        if (schema.type === 'number') {
            formattedValue = parseInt(value as string)
        }

        const selected = (schema.oneOf as JsonSchema4[]).find(
            (option: JsonSchema4) => option.const === formattedValue
        )
        if (selected) {
            handleChange(path, selected.const)
        }
    }

    const options = (schema.oneOf as Record<string, string>[]).map((option) => ({
        title: t(`options.${uischema.options?.name}.${option.const}`),
        const: option.const,
        icon: option.icon,
    }))

    return (
        <NativeDropdown
            value={value}
            onChange={onChange}
            errors={errors}
            required={required}
            placeholder={t(uischema.options?.placeholder)}
            options={options}
            path={path}
        />
    )
}

export const NativeDropdownTester = rankWith(
    4, //increase rank as needed
    uiTypeIs('NativeDropdown')
)

export default withJsonFormsControlProps(NativeDropdownRenderer)
